import { useContext } from 'react';
import { CalendarClock, Gauge, MessageSquare, Store } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../../sporttia/all';
import { getPath } from '../../../pages/Pages';
import { PERMISSIONS } from '../../../lib/permissions';
import { usePermission } from '../../../lib/usePermission';

export interface QuickAccessShortcut {
    /** Ancla para el tour guiado y clave de lista. */
    id: string;
    label: string;
    icon: React.ReactNode;
    /** Destino, para que el enlace se pueda abrir en otra pestaña o ventana. */
    href: string;
}

/**
 * Accesos rápidos del layout: los mismos cuatro que en el sidebar de
 * sporttia-competitions (punto de venta, monitor de aforo, disponibilidad y
 * mensajería), con sus mismos iconos. Los comparten el pie del sidebar y la
 * barra inferior de móvil.
 *
 * Todos son páginas con su propia ruta, así que basta con el destino: quien los
 * pinta los saca como enlaces y la navegación (clic normal, otra pestaña, otra
 * ventana) sale de ahí.
 *
 * Con los roles v8 encendidos cada acceso exige además el permiso de ver su
 * pantalla; con ellos apagados `can` siempre deja pasar.
 */
export function useQuickAccessShortcuts(): QuickAccessShortcut[] {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { can } = usePermission();

    const modules: string[] = cxt?.sc?.modules ?? [];
    const shortcuts: QuickAccessShortcut[] = [];

    if (
        modules.includes('salePoint') &&
        !cxt.zero &&
        can(PERMISSIONS.SALE_POINT.VIEW)
    ) {
        shortcuts.push({
            id: 'salePoint',
            label: t('sidebar.shortcuts.pos'),
            icon: <Store className="h-4 w-4" />,
            href: getPath('salePoint'),
        });
    }

    if (
        modules.includes('device') &&
        !cxt.zero &&
        can(PERMISSIONS.DEVICE.VIEW)
    ) {
        shortcuts.push({
            id: 'capacityMonitor',
            label: t('sidebar.shortcuts.capacityMonitor'),
            icon: <Gauge className="h-4 w-4" />,
            href: getPath('capacityMonitor'),
        });
    }

    if (can(PERMISSIONS.BOOKING.VIEW)) {
        shortcuts.push({
            id: 'timetable',
            label: t('sidebar.shortcuts.availability'),
            icon: <CalendarClock className="h-4 w-4" />,
            href: getPath('timetable', { id: cxt?.sc?.id }),
        });
    }

    if (can(PERMISSIONS.COMMUNICATION.VIEW)) {
        shortcuts.push({
            id: 'messages',
            label: t('sidebar.shortcuts.messaging'),
            icon: <MessageSquare className="h-4 w-4" />,
            href: getPath('scMessages'),
        });
    }

    return shortcuts;
}
